import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/index'
import { BillingProcedure, LedgerEntry } from '@dataTypes/billing.types'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import { AffectedBalance } from '@services/api/billing/adjustment.api'
import { createAdjustment } from '@store/actions/adjustment.action'
import {
  fetchARAging,
  fetchInsuranceARAnalytics,
  fetchLedgerEntries,
  fetchPatientARAnalytics,
} from '@store/actions/billing.action'
import { getAllAdjustmentTypeSettings } from '@store/actions/adjustmentType.action'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { formatCurrency } from '@utils/billing.utils'
import { GlobalLoadingActions } from '@context/GlobalLoadingContext'
import { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'
import { logger } from '@utils/safeLogger'

const ADJUSTMENT_LOADING_CONTAINER = '.ds-modal'

const roundToCents = (amount: number): number => Math.round(amount * 100) / 100

const toBalance = (value: number | null | undefined): number =>
  roundToCents(Math.abs(Number(value) || 0))

/** Mirrors balance caps in billing-module adjustment.service.ts using ledger procedure fields. */
const getAdjustmentLimitError = (
  procedure: BillingProcedure,
  insuranceSigned: number | null,
  patientSigned: number | null,
): string | null => {
  const insuranceAR = toBalance(procedure.insuranceBalance)
  const patientAR = toBalance(procedure.patientBalance)
  const availableAdjustment = roundToCents(
    toBalance(procedure.insuranceAdjustment) +
      toBalance(procedure.patientAdjustment),
  )
  const code = procedure.procedureCode || procedure.procedureLogId

  const hasInsurance = insuranceSigned !== null && insuranceSigned !== 0
  const hasPatient = patientSigned !== null && patientSigned !== 0
  if (!hasInsurance && !hasPatient) {
    return null
  }

  const insuranceAbs = hasInsurance
    ? roundToCents(Math.abs(insuranceSigned as number))
    : 0
  const patientAbs = hasPatient
    ? roundToCents(Math.abs(patientSigned as number))
    : 0
  const insuranceDecrease = hasInsurance && (insuranceSigned as number) < 0
  const insuranceIncrease = hasInsurance && (insuranceSigned as number) > 0
  const patientDecrease = hasPatient && (patientSigned as number) < 0
  const patientIncrease = hasPatient && (patientSigned as number) > 0

  // Patient decrease + insurance increase (direct transfer)
  if (patientDecrease && insuranceIncrease) {
    const directTransfer = roundToCents(Math.min(patientAbs, insuranceAbs))
    if (directTransfer > patientAR) {
      return `Patient adjustment for ${code} cannot exceed the patient balance of ${formatCurrency(patientAR)}.`
    }
    const insuranceRemainder = Math.max(
      0,
      roundToCents(insuranceAbs - directTransfer),
    )
    if (insuranceRemainder > availableAdjustment) {
      return `Insurance adjustment for ${code} cannot exceed the available adjustment balance of ${formatCurrency(availableAdjustment)}.`
    }
    return null
  }

  // Insurance decrease + patient increase (direct transfer)
  if (insuranceDecrease && patientIncrease) {
    const directTransfer = roundToCents(Math.min(insuranceAbs, patientAbs))
    if (directTransfer > insuranceAR) {
      return `Insurance adjustment for ${code} cannot exceed the insurance balance of ${formatCurrency(insuranceAR)}.`
    }
    const patientRemainder = Math.max(
      0,
      roundToCents(patientAbs - directTransfer),
    )
    if (patientRemainder > availableAdjustment) {
      return `Patient adjustment for ${code} cannot exceed the available adjustment balance of ${formatCurrency(availableAdjustment)}.`
    }
    return null
  }

  if (insuranceDecrease && insuranceAbs > insuranceAR) {
    return `Insurance adjustment for ${code} cannot exceed the insurance balance of ${formatCurrency(insuranceAR)}.`
  }
  if (insuranceIncrease && insuranceAbs > availableAdjustment) {
    return `Insurance adjustment for ${code} cannot exceed the available adjustment balance of ${formatCurrency(availableAdjustment)}.`
  }
  if (patientDecrease && patientAbs > patientAR) {
    return `Patient adjustment for ${code} cannot exceed the patient balance of ${formatCurrency(patientAR)}.`
  }
  if (patientIncrease && patientAbs > availableAdjustment) {
    return `Patient adjustment for ${code} cannot exceed the available adjustment balance of ${formatCurrency(availableAdjustment)}.`
  }

  return null
}

type AdjustmentField = 'insuranceAdjustment' | 'patientAdjustment'

type ProcedureAdjustmentEntry = {
  insuranceAdjustment?: number
  patientAdjustment?: number
}

type AdjustmentDrafts = Record<string, Partial<Record<AdjustmentField, string>>>

const applyAdjustmentText = (
  prev: Record<string, ProcedureAdjustmentEntry>,
  procedureLogId: string,
  field: AdjustmentField,
  value: string,
): Record<string, ProcedureAdjustmentEntry> => {
  if (value === '' || value === null || value === undefined) {
    const current = prev[procedureLogId]
    if (!current) {
      return prev
    }
    const updated: ProcedureAdjustmentEntry = {
      ...current,
      [field]: undefined,
    }
    if (
      updated.insuranceAdjustment === undefined &&
      updated.patientAdjustment === undefined
    ) {
      const newState = { ...prev }
      delete newState[procedureLogId]
      return newState
    }
    return {
      ...prev,
      [procedureLogId]: updated,
    }
  }

  const numValue = parseFloat(value)
  if (isNaN(numValue)) {
    return prev
  }

  const roundedValue = Math.round(numValue * 100) / 100
  return {
    ...prev,
    [procedureLogId]: {
      insuranceAdjustment: prev[procedureLogId]?.insuranceAdjustment,
      patientAdjustment: prev[procedureLogId]?.patientAdjustment,
      [field]: roundedValue,
    },
  }
}

const clearAdjustmentDraftField = (
  prev: AdjustmentDrafts,
  procedureLogId: string,
  field: AdjustmentField,
): AdjustmentDrafts => {
  const entry = prev[procedureLogId]
  if (!entry || entry[field] === undefined) {
    return prev
  }

  const nextEntry = { ...entry }
  delete nextEntry[field]

  const next = { ...prev }
  if (Object.keys(nextEntry).length === 0) {
    delete next[procedureLogId]
  } else {
    next[procedureLogId] = nextEntry
  }
  return next
}

const parseDraftPreviewAmount = (value: string): number | undefined | null => {
  if (value === '') {
    return null
  }
  const numValue = parseFloat(value)
  if (!Number.isFinite(numValue)) {
    return undefined
  }
  return roundToCents(numValue)
}

/** Committed amounts with in-progress draft text overlaid for live preview. */
const mergePreviewAdjustments = (
  committed: Record<string, ProcedureAdjustmentEntry>,
  drafts: AdjustmentDrafts,
): Record<string, ProcedureAdjustmentEntry> => {
  const preview: Record<string, ProcedureAdjustmentEntry> = { ...committed }

  for (const [procedureLogId, fields] of Object.entries(drafts)) {
    let entry: ProcedureAdjustmentEntry = {
      ...preview[procedureLogId],
    }

    for (const field of ['insuranceAdjustment', 'patientAdjustment'] as const) {
      if (fields[field] === undefined) {
        continue
      }
      const parsed = parseDraftPreviewAmount(fields[field] as string)
      if (parsed === undefined) {
        continue
      }
      if (parsed === null) {
        entry = { ...entry, [field]: undefined }
      } else {
        entry = { ...entry, [field]: parsed }
      }
    }

    if (
      entry.insuranceAdjustment === undefined &&
      entry.patientAdjustment === undefined
    ) {
      delete preview[procedureLogId]
    } else {
      preview[procedureLogId] = entry
    }
  }

  return preview
}

interface UseApplyAdjustmentModalReturn {
  insuranceAdjustmentType: string
  patientAdjustmentType: string
  loading: boolean
  error: string | null

  procedures: BillingProcedure[]
  procedureTotals: {
    clinicFee: number
    approvedInsurance: number
    insuranceAdjustment: number
    patientResponsibility: number
    patientAdjustment: number
  }
  insuranceBalance: number
  patientBalance: number
  summaryValues: {
    charges: number
    appointmentAdjustment: number
    totalAppointmentBalance: number
  }
  insuranceAdjustmentTypes: Array<{
    adjustment_type_id: string
    name: string
    modifier: 'Positive' | 'Negative'
  }>
  patientAdjustmentTypes: Array<{
    adjustment_type_id: string
    name: string
    modifier: 'Positive' | 'Negative'
  }>
  notes: Array<Record<string, unknown>>
  handleUpdateNotes: (data: Array<Record<string, unknown>>) => void
  adjustmentDrafts: AdjustmentDrafts

  setInsuranceAdjustmentType: (value: string) => void
  setPatientAdjustmentType: (value: string) => void
  handleAdjustmentChange: (
    procedureLogId: string,
    field: AdjustmentField,
    value: string,
  ) => void
  handleAdjustmentBlur: (
    procedureLogId: string,
    field: AdjustmentField,
    value: string,
  ) => void
  handleApply: () => Promise<void>
}

const useApplyAdjustmentModal = (
  ledgerEntry: LedgerEntry | null,
  isVisible: boolean,
  onClose: () => void,
  showToast: (notification: NotificationToastData) => void,
  showLoading: GlobalLoadingActions['showLoading'],
  hideLoading: GlobalLoadingActions['hideLoading'],
): UseApplyAdjustmentModalReturn => {
  const dispatch = useDispatch<AppDispatch>()
  const { adjustmentTypes } = useSelector(
    (state: RootState) => state.adjustmentType,
  )
  const { loading: adjustmentLoading, error: adjustmentError } = useSelector(
    (state: RootState) => state.adjustment,
  )

  const [insuranceAdjustmentType, setInsuranceAdjustmentType] =
    useState<string>('')
  const [patientAdjustmentType, setPatientAdjustmentType] = useState<string>('')
  const [procedureAdjustments, setProcedureAdjustments] = useState<
    Record<string, ProcedureAdjustmentEntry>
  >({})
  const [adjustmentDrafts, setAdjustmentDrafts] = useState<AdjustmentDrafts>({})
  const [notes, setNotes] = useState<Array<Record<string, unknown>>>([])

  // Initialize and reset form when modal opens
  useEffect(() => {
    if (!isVisible) return

    const initializeModal = async (): Promise<void> => {
      try {
        showLoading({ container: ADJUSTMENT_LOADING_CONTAINER })
        await dispatch(getAllAdjustmentTypeSettings()).unwrap()
      } catch (error) {
        logger.error('Error fetching adjustment type settings:', error)
      } finally {
        setInsuranceAdjustmentType('')
        setPatientAdjustmentType('')
        setProcedureAdjustments({})
        setAdjustmentDrafts({})
        setNotes([])
        hideLoading(ADJUSTMENT_LOADING_CONTAINER)
      }
    }

    initializeModal()
  }, [isVisible, dispatch, showLoading, hideLoading])

  const previewAdjustments = useMemo(
    () => mergePreviewAdjustments(procedureAdjustments, adjustmentDrafts),
    [procedureAdjustments, adjustmentDrafts],
  )

  // Merge procedures with preview adjustment values (committed + drafts)
  const procedures = useMemo((): BillingProcedure[] => {
    if (!ledgerEntry) return []
    return (ledgerEntry.procedures || []).map((proc) => ({
      ...proc,
      insuranceAdjustment:
        previewAdjustments[proc.procedureLogId]?.insuranceAdjustment ?? null,
      patientAdjustment:
        previewAdjustments[proc.procedureLogId]?.patientAdjustment ?? null,
    }))
  }, [ledgerEntry, previewAdjustments])

  // Calculate procedure totals
  const procedureTotals = useMemo(() => {
    return procedures.reduce(
      (acc, proc) => ({
        clinicFee: acc.clinicFee + (proc.clinicFee || 0),
        approvedInsurance:
          acc.approvedInsurance + (proc.approvedInsurance || 0),
        insuranceAdjustment:
          acc.insuranceAdjustment + (proc.insuranceAdjustment || 0),
        patientResponsibility:
          acc.patientResponsibility + (proc.patientResponsibility || 0),
        patientAdjustment:
          acc.patientAdjustment + (proc.patientAdjustment || 0),
      }),
      {
        clinicFee: 0,
        approvedInsurance: 0,
        insuranceAdjustment: 0,
        patientResponsibility: 0,
        patientAdjustment: 0,
      },
    )
  }, [procedures])

  // Calculate insurance and patient balances for badges
  const insuranceBalance = useMemo(() => {
    return procedures.reduce(
      (acc, proc) => acc + (proc.insuranceBalance || 0),
      0,
    )
  }, [procedures])

  const patientBalance = useMemo(
    () => procedures.reduce((acc, proc) => acc + (proc.patientBalance || 0), 0),
    [procedures],
  )

  const handleAdjustmentChange = useCallback(
    (procedureLogId: string, field: AdjustmentField, value: string): void => {
      setAdjustmentDrafts((prev) => ({
        ...prev,
        [procedureLogId]: {
          ...prev[procedureLogId],
          [field]: value,
        },
      }))
    },
    [],
  )

  const handleAdjustmentBlur = useCallback(
    (procedureLogId: string, field: AdjustmentField, value: string): void => {
      setProcedureAdjustments((prev) =>
        applyAdjustmentText(prev, procedureLogId, field, value),
      )
      setAdjustmentDrafts((prev) =>
        clearAdjustmentDraftField(prev, procedureLogId, field),
      )
    },
    [],
  )

  const insuranceAdjustmentTypes = useMemo(() => {
    return adjustmentTypes.filter(
      (type) =>
        type.category === 'Adjustment' && type.status === SettingStatus.Enabled,
    )
  }, [adjustmentTypes])

  const patientAdjustmentTypes = useMemo(() => {
    return adjustmentTypes.filter(
      (type) =>
        type.category === 'Adjustment' && type.status === SettingStatus.Enabled,
    )
  }, [adjustmentTypes])

  const handleUpdateNotes = useCallback(
    (data: Array<Record<string, unknown>>): void => {
      setNotes(data)
    },
    [],
  )

  // Return adjustment types data only (no JSX)

  // Calculate summary values from preview (committed + drafts)
  const summaryValues = useMemo(() => {
    if (!ledgerEntry) {
      return {
        charges: 0,
        appointmentAdjustment: 0,
        totalAppointmentBalance: 0,
      }
    }

    const initialAppointmentAdjustment = ledgerEntry.appointmentAdjustment || 0
    let adjustmentImpact = 0
    let balanceImpact = 0

    Object.entries(previewAdjustments).forEach(([, adj]) => {
      const insuranceAmount = adj.insuranceAdjustment || 0
      const patientAmount = adj.patientAdjustment || 0

      if (insuranceAmount !== 0 && insuranceAdjustmentType) {
        const adjustmentType = adjustmentTypes.find(
          (type) => type.adjustment_type_id === insuranceAdjustmentType,
        )
        if (adjustmentType) {
          if (adjustmentType.modifier === 'Negative') {
            adjustmentImpact += insuranceAmount
            balanceImpact -= insuranceAmount
          } else {
            adjustmentImpact -= insuranceAmount
            balanceImpact += insuranceAmount
          }
        }
      }

      if (patientAmount !== 0 && patientAdjustmentType) {
        const adjustmentType = adjustmentTypes.find(
          (type) => type.adjustment_type_id === patientAdjustmentType,
        )
        if (adjustmentType) {
          if (adjustmentType.modifier === 'Negative') {
            adjustmentImpact += patientAmount
            balanceImpact -= patientAmount
          } else {
            adjustmentImpact -= patientAmount
            balanceImpact += patientAmount
          }
        }
      }
    })

    const charges = ledgerEntry.clinicFee || 0
    const appointmentAdjustment =
      initialAppointmentAdjustment + adjustmentImpact
    const totalAppointmentBalance =
      (ledgerEntry.appointmentBalance || 0) + balanceImpact

    return {
      charges,
      appointmentAdjustment,
      totalAppointmentBalance,
    }
  }, [
    ledgerEntry,
    previewAdjustments,
    insuranceAdjustmentType,
    patientAdjustmentType,
    adjustmentTypes,
  ])

  const handleApply = useCallback(async (): Promise<void> => {
    if (!ledgerEntry) return

    let committedAdjustments = procedureAdjustments
    const draftEntries = Object.entries(adjustmentDrafts)
    if (draftEntries.length > 0) {
      committedAdjustments = { ...procedureAdjustments }
      for (const [procedureLogId, fields] of draftEntries) {
        for (const field of [
          'insuranceAdjustment',
          'patientAdjustment',
        ] as const) {
          if (fields[field] !== undefined) {
            committedAdjustments = applyAdjustmentText(
              committedAdjustments,
              procedureLogId,
              field,
              fields[field] as string,
            )
          }
        }
      }
      setProcedureAdjustments(committedAdjustments)
      setAdjustmentDrafts({})
    }

    const procedureAdjustmentsArray: Array<{
      procedureLogId: string
      amount: number
      adjustmentTypeId: string
      affectedBalance: AffectedBalance
    }> = []

    const originalProceduresById = new Map(
      (ledgerEntry.procedures || []).map((procedure) => [
        procedure.procedureLogId,
        procedure,
      ]),
    )

    const insuranceType = adjustmentTypes.find(
      (type) => type.adjustment_type_id === insuranceAdjustmentType,
    )
    const patientType = adjustmentTypes.find(
      (type) => type.adjustment_type_id === patientAdjustmentType,
    )

    const procedureLogIds = Object.keys(committedAdjustments)

    for (const procedureLogId of procedureLogIds) {
      const adjustments = committedAdjustments[procedureLogId]
      const insuranceAmount = adjustments.insuranceAdjustment || 0
      const patientAmount = adjustments.patientAdjustment || 0

      let insuranceSigned: number | null = null
      let patientSigned: number | null = null

      if (insuranceAmount !== 0) {
        if (!insuranceAdjustmentType) {
          const errorMessage =
            'Insurance adjustment type is required when entering insurance adjustments.'
          showToast(createFailedToast('Validation error', errorMessage))
          return
        }
        insuranceSigned =
          insuranceType?.modifier === 'Negative'
            ? -Math.abs(insuranceAmount)
            : Math.abs(insuranceAmount)

        procedureAdjustmentsArray.push({
          procedureLogId,
          amount: insuranceSigned,
          adjustmentTypeId: insuranceAdjustmentType,
          affectedBalance: AffectedBalance.INSURANCE,
        })
      }

      if (patientAmount !== 0) {
        if (!patientAdjustmentType) {
          const errorMessage =
            'Patient adjustment type is required when entering patient adjustments.'
          showToast(createFailedToast('Validation error', errorMessage))
          return
        }
        patientSigned =
          patientType?.modifier === 'Negative'
            ? -Math.abs(patientAmount)
            : Math.abs(patientAmount)

        procedureAdjustmentsArray.push({
          procedureLogId,
          amount: patientSigned,
          adjustmentTypeId: patientAdjustmentType,
          affectedBalance: AffectedBalance.PATIENT,
        })
      }

      const originalProcedure = originalProceduresById.get(procedureLogId)
      if (originalProcedure) {
        const limitError = getAdjustmentLimitError(
          originalProcedure,
          insuranceSigned,
          patientSigned,
        )
        if (limitError) {
          showToast(createFailedToast('Validation error', limitError))
          return
        }
      }
    }

    if (procedureAdjustmentsArray.length === 0) {
      const errorMessage = 'Please enter at least one adjustment amount.'
      showToast(createFailedToast('Validation error', errorMessage))
      return
    }

    try {
      showLoading({ container: ADJUSTMENT_LOADING_CONTAINER })
      await dispatch(
        createAdjustment({
          appointmentId: ledgerEntry.appointmentId,
          procedureAdjustments: procedureAdjustmentsArray,
        }),
      ).unwrap()

      await dispatch(fetchPatientARAnalytics()).unwrap()
      await dispatch(fetchInsuranceARAnalytics()).unwrap()
      await dispatch(fetchARAging()).unwrap()
      await dispatch(fetchLedgerEntries()).unwrap()

      showToast(
        createSuccessToast(
          'Adjustment applied successfully',
          'Adjustment has been applied successfully.',
        ),
      )
      onClose()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create adjustment'
      showToast(createFailedToast('Adjustment not applied', errorMessage))
    } finally {
      hideLoading(ADJUSTMENT_LOADING_CONTAINER)
    }
  }, [
    ledgerEntry,
    procedureAdjustments,
    adjustmentDrafts,
    insuranceAdjustmentType,
    patientAdjustmentType,
    adjustmentTypes,
    dispatch,
    onClose,
    showToast,
    showLoading,
    hideLoading,
  ])

  return {
    insuranceAdjustmentType,
    patientAdjustmentType,
    loading: adjustmentLoading,
    error: adjustmentError,
    procedures,
    procedureTotals,
    insuranceBalance,
    patientBalance,
    summaryValues,
    insuranceAdjustmentTypes,
    patientAdjustmentTypes,
    notes,
    handleUpdateNotes,
    adjustmentDrafts,
    setInsuranceAdjustmentType,
    setPatientAdjustmentType,
    handleAdjustmentChange,
    handleAdjustmentBlur,
    handleApply,
  }
}

export { useApplyAdjustmentModal as useApplyAdjustment }
export default useApplyAdjustmentModal
